import React, { useRef, useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import { DrawerLayout, TaskLayout } from '@layouts';
import {
  MaintenanceForm,
  CommentsV2,
  ActivityListV2,
  TaskCarDetails,
  TaskDetailScrollableSection,
  MoreButton,
  PermissionsGate,
  TabsV2,
} from '@components';
import {
  UploadARNMImageV2,
  UploadARNMDocumentV2,
} from 'components/common/media';

import { useArnmMaintenanceTaskOperations } from '@hooks';
import { SCOPES } from '@constants';
import { asyncForEach, showError } from '@utils';
import { boardsServices } from '@services';
import {
  ARNM_MAINTENANCE_TASK_FIELD_NAMES,
  ARNM_MAINTENANCE_TASK_STATUSES_NAMES,
} from 'components/arnmMaintenance/constants';
import { TaskSection } from 'components/common/TaskSection';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { ButtonV2 } from 'components/common/ui';
import { getArnmMaintenanceTaskPayload } from 'hooks/arnmMaintenance/utils';

export const tabs = [
  {
    label: 'Ticket Details',
    id: 'ticket-details',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Work Order',
    id: 'work-order',
    icon: <BuildIcon data-testid="BuildIcon" />,
  },
  {
    label: 'Financial Details',
    id: 'financial-details',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    icon: <FormatListBulletedIcon data-testid="FormatListBulletedIcon" />,
    id: 'ticket-details-right',
  },
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
  },
  { icon: <InsertPhotoIcon data-testid="InsertPhotoIcon" />, id: 'images' },
];

const ArnmMaintenanceTask = () => {
  const {
    task,
    vehicle,
    taskId,
    stages,
    loading,
    assignees,
    arnmAssignees,
    servicePoints,
    getArnmMaintenanceTask,
    updateTask,
  } = useArnmMaintenanceTaskOperations();
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const submitBtnRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChangeStatus = async (id) => {
    const payload = {
      status: Number(id),
    };
    try {
      setIsSubmitting(true);
      await boardsServices.updateTask(taskId, payload);

      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderHeaderOptions = () => {
    return (
      <PermissionsGate>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <MoreButton
            moreOptions={[
              {
                title: 'Cancel Ticket',
                onClick: () => handleChangeStatus(12), // Cancelled
                scopes: [SCOPES.canEdit],
              },
            ]}
          />
          <ButtonV2
            type="submit"
            size="small"
            form="maintenance-form"
            ref={submitBtnRef}
            disabled={loading || isSubmitting}
          >
            Save
          </ButtonV2>
        </Box>
      </PermissionsGate>
    );
  };

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we save the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.addTaskComment(taskId, comment);

      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we edit the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.editTaskComment(taskId, commentId, comment);
      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we delete the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.deleteTaskComment(taskId, commentId);
      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskImage(taskId, imageId);
      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskDocument(taskId, docId);
      await getArnmMaintenanceTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeTicketDetails = async ({ field, value }) => {
    const payload = {
      [field]: value,
    };

    try {
      setIsSubmitting(true);
      await boardsServices.updateTask(taskId, payload);

      await getArnmMaintenanceTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (form) => {
    const imagesToupLoad = images?.filter((img) => img?.localFile);
    const documentsToUpLoad = [...documents]?.filter((doc) => doc?.localFile);
    const documentsToPatch = [...documents]?.filter(
      (doc) => doc?.isPatched && !doc?.localFile
    );
    const imagesToPatch = images?.filter(
      (img) => img?.isPatched && !img?.localFile
    );
    try {
      setIsSubmitting(true);
      const values = getArnmMaintenanceTaskPayload(form);

      await updateTask({
        id: taskId,
        ...values,
      });

      await asyncForEach(imagesToupLoad, async (img) => {
        await boardsServices.uploadTaskImage(taskId, img?.file, img?.tags);
      });
      await asyncForEach(imagesToPatch, async (img) => {
        await boardsServices.updateTaskImage(taskId, img?.id, {
          tags: img?.tags,
        });
      });
      await asyncForEach(documentsToUpLoad, async (doc) => {
        await boardsServices.uploadTaskDocument(
          taskId,
          doc?.file,
          '',
          doc?.tags
        );
      });
      await asyncForEach(documentsToPatch, async (doc) => {
        await boardsServices.updateTaskDocument(taskId, doc?.id, {
          tags: doc?.tags,
        });
      });
      await getArnmMaintenanceTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStage = async (id) => {
    const payload = {
      status: Number(id),
    };
    try {
      setIsSubmitting(true);
      await submitBtnRef?.current?.click?.();
      await boardsServices.updateTask(taskId, payload);

      await getArnmMaintenanceTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const taskStatusNames = Object.keys(
    ARNM_MAINTENANCE_TASK_STATUSES_NAMES
  ).reduce((acc, cur) => {
    acc[cur] = ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].label;

    return acc;
  }, {});

  return (
    <>
      <Head>
        <title>{`Maintenance/Maintenance: ${taskId} `}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={taskId}
        entity={ENTITIES_PERMISIONS.ReadRepairs}
        renderHeaderOptions={renderHeaderOptions}
        isLoading={loading || isSubmitting}
        stages={stages}
        currentStage={task?.status?.value}
        onChangeStage={handleChangeStage}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="arnmMaintenance-task-details" />

            <TaskDetailScrollableSection id="arnmMaintenance-task-details">
              <MaintenanceForm
                isOutDate={task?.status?.value === 14}
                onSubmit={handleSubmit}
                defaultValues={task}
                loading={loading || isSubmitting}
                assignees={assignees}
                servicePoints={servicePoints}
              />
              <TaskSection title="Comments" id={tabs[3].id} icon={tabs[3].icon}>
                <CommentsV2
                  comments={task?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={assignees}
                />
              </TaskSection>
              <TaskSection title="History" id={tabs[4].id} icon={tabs[4].icon}>
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={ARNM_MAINTENANCE_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2
              tabs={rightTabs}
              observerRootId="arnmMaintenance-task-right"
            />
            <TaskDetailScrollableSection id="arnmMaintenance-task-right">
              <TaskSection
                isSmall
                title="Ticket Details"
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TaskCarDetails
                  task={task}
                  assignees={arnmAssignees}
                  stages={stages}
                  onChange={handleChangeTicketDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Car Details"
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TaskCarDetails vehicle={vehicle} />
              </TaskSection>
              <TaskSection
                isSmall
                title="Files"
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadARNMDocumentV2
                  documents={task?.documents}
                  onChange={setDocuments}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || isSubmitting}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Images"
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
              >
                <UploadARNMImageV2
                  images={task?.images}
                  onChange={setImages}
                  onDeleteImage={handleDeleteImage}
                  loading={loading || isSubmitting}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

ArnmMaintenanceTask.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default ArnmMaintenanceTask;
